import { FC, Fragment, useCallback, useMemo } from 'react'
import { StageOrder, compareAssignmentStageOrder } from 'utils/stages'

import AssignmentStageAdvice from 'components/common/AssignmentStageAdvice/AssignmentStageAdvice'
import CheckIcon from '@mui/icons-material/Check'
import { MUIButton } from 'components/common/MUIButton'
import { Stack } from '@mui/material'
import { useActionPopup } from 'utils/hooks'
import { useGalleryAssignment } from 'components/pages/Gallery/_main/contexts/GalleryAssignment.context'
import { useGalleryConstants } from 'components/pages/Gallery/_main/contexts/GalleryConstants.context'
import { useSendDocumentsToClient } from 'dataQueries'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'components/contexts/SnackbarService.context'

interface Props {
  /** If we allow sending assignment to the client, based on external condition */
  canSendAssignmentDocumentsToClient: Boolean,
  /** Assignment ID, which should be sent out */
  assignmentId: string,
}

export const SendDocumentsToClient: FC<Props> = ({
  canSendAssignmentDocumentsToClient,
  assignmentId,
}) => {
  const { t } = useTranslation(['gallery'])
  const { showConfirm } = useActionPopup()
  const { stagesUnlockingSendAssignmentDocumentsToClient } = useGalleryConstants()
  const { assignmentStage } = useGalleryAssignment()
  const sendDocumentsToClient = useSendDocumentsToClient()
  const { spawnSuccessToast } = useSnackbar()

  const canBeSendToClient = useMemo(() => {
    return canSendAssignmentDocumentsToClient && (!sendDocumentsToClient || sendDocumentsToClient.isIdle)
  }, [canSendAssignmentDocumentsToClient, sendDocumentsToClient])

  const assignmentDocumentsAlreadySentToClient = useMemo(() =>
    (!!assignmentStage && (compareAssignmentStageOrder(assignmentStage, stagesUnlockingSendAssignmentDocumentsToClient) === StageOrder.COMPARING_AFTER_COMPARED_TO)),
    [assignmentStage, stagesUnlockingSendAssignmentDocumentsToClient]
  )

  const cannotSendAssignmentDocumentsToClientInfo = useMemo(() => {
    if ((!(!sendDocumentsToClient || sendDocumentsToClient.isIdle)) || assignmentDocumentsAlreadySentToClient) {
      return t('sent_to_client')
    } else if (!assignmentStage || !stagesUnlockingSendAssignmentDocumentsToClient.has(assignmentStage)) {
      return <AssignmentStageAdvice id="send_to_client" currentStage={assignmentStage} validStages={stagesUnlockingSendAssignmentDocumentsToClient} />
    } else {
      return ''
    }
  }, [assignmentStage, assignmentDocumentsAlreadySentToClient, sendDocumentsToClient, stagesUnlockingSendAssignmentDocumentsToClient, t])

  const initiateSendAssignmentVisualsToClient = useCallback(async () => {
    if (!canSendAssignmentDocumentsToClient) return
    if (!(await showConfirm(t('send_documents_to_client_prompt')))) return
    sendDocumentsToClient.mutate(
      { assignmentId },
      {
        onSuccess: () => {
          spawnSuccessToast(t('sent_to_client'))
        },
      }
    )
  }, [assignmentId, canSendAssignmentDocumentsToClient, sendDocumentsToClient, showConfirm, spawnSuccessToast, t])

  return (
    <Fragment>
      <Stack alignItems="center" direction="row" flexWrap="wrap" mb="2rem" gap={1}>
        <MUIButton
          startIcon={<CheckIcon />}
          type="greenPrimary"
          disabled={!canBeSendToClient}
          onClick={initiateSendAssignmentVisualsToClient}
        >
          <span>{t('send_to_client')}</span>
        </MUIButton>

        {!canBeSendToClient && cannotSendAssignmentDocumentsToClientInfo &&
          <span>{cannotSendAssignmentDocumentsToClientInfo}</span>
        }
      </Stack>
    </Fragment>
  )
}
